import { RoleConfig, WeightedRules, WeightedSkill } from "./types";

const renderSkills = (skills: WeightedSkill[]) => {
  if (!skills.length) return "- No skills configured.";
  return skills
    .map((skill, index) => `- ${index + 1}. ${skill.name || "Unnamed skill"} (Weight: ${skill.weight})`)
    .join("\n");
};

const renderRules = (label: string, rules: WeightedRules) => {
  const content = rules.rules.length
    ? rules.rules.map((rule, idx) => `  - ${idx + 1}. ${rule}`).join("\n")
    : "  - No rules configured.";

  return `${label} (Weight: ${rules.weight})\n${content}`;
};

const renderTier = (tierName: string, min: number, requirements: string[]) => {
  const req = requirements.length
    ? requirements.map((item, idx) => `  - ${idx + 1}. ${item}`).join("\n")
    : "  - No requirements configured.";

  return `${tierName.toUpperCase()} (Min Score: ${min})\n${req}`;
};

export const buildPrompt = (config: RoleConfig, overrideRoleName?: string): string => {
  const sections = config.customSections || [];
  
  const getSectionsByPosition = (pos: string) => {
    const matched = sections.filter(s => s.position === pos);
    if (!matched.length) return [];
    return matched.map(s => `\n${s.title ? `## ${s.title}\n` : ""}${s.content}\n`);
  };

  const roleNameDisplay = overrideRoleName || config.roleName;

  return [
    ...getSectionsByPosition("top"),
    `You are evaluating a candidate CV for the role: ${roleNameDisplay}.`,
    "Score all dimensions independently and produce a final score out of 100.",
    "",
    ...getSectionsByPosition("before_scoring"),
    "SCORING CRITERIA",
    "Skills",
    renderSkills(config.scoring.skills),
    "",
    renderRules("Experience", config.scoring.experience),
    "",
    renderRules("Projects", config.scoring.projects),
    "",
    renderRules("Extra Signals", config.scoring.extra),
    "",
    ...getSectionsByPosition("before_tiers"),
    "TIER CLASSIFICATION",
    renderTier("tier1", config.tiers.tier1.min, config.tiers.tier1.requirements),
    "",
    renderTier("tier2", config.tiers.tier2.min, config.tiers.tier2.requirements),
    "",
    renderTier("tier3", config.tiers.tier3.min, config.tiers.tier3.requirements),
    "",
    ...getSectionsByPosition("before_keywords"),
    "KEYWORD SIGNALS",
    "Positive Signals JSON:",
    JSON.stringify(config.keywords.positive, null, 2),
    "Negative Signals JSON:",
    JSON.stringify(config.keywords.negative, null, 2),
    "",
    "OUTPUT REQUIREMENTS",
    ...(config.outputRequirements ?? [
      "Return a concise reasoning summary.",
      "Return final score as a number.",
      "Return assigned tier (tier1, tier2, or tier3).",
      "Mention strongest positive and risk signals."
    ]).map((req, idx) => `${idx + 1}. ${req}`),
    ...getSectionsByPosition("bottom")
  ].filter(Boolean).join("\n").replace(/\n{3,}/g, '\n\n'); // clean up extra newlines
};
